<template>
  <teleport to="body">
    <WdsModal
      :show="open"
      :title="title"
      disable-click-outside
      :data-test="dataTest"
      @cancel="emit('cancel')"
    >
      <WdsStack direction-column gap-md>
        <FormCustomFields
          :fields="fields"
          :field-values="fieldValues"
          :field-value-fragments="fieldValueFragments"
          :errors="errors"
          :loading="loading"
          :disable-inputs="saving"
          :disable-worker-input-fields="disableWorkerInputFields"
          @update:field-values="emit('update:field-values', $event)"
        />
        <WdsText v-if="failed" error sm :data-test="dataTest ? `${dataTest}-error` : undefined">
          {{ $t('An error occurred, please try again.') }}
        </WdsText>
      </WdsStack>

      <template v-if="!hideFooter" #footer>
        <WdsStack justify-end align-center gap-sm class="tw-w-full">
          <WdsSpinner v-if="saving" indeterminate />
          <WdsButton :disabled="saving" @click="emit('cancel')">
            {{ $t('Cancel') }}
          </WdsButton>
          <WdsButton
            primary
            :disabled="saving"
            :data-test="dataTest ? `${dataTest}-save` : undefined"
            @click="emit('save')"
          >
            {{ $t('Save') }}
          </WdsButton>
        </WdsStack>
      </template>
    </WdsModal>
  </teleport>
</template>

<script setup lang="ts">
import { Ref } from 'vue'
import { WdsButton, WdsModal, WdsSpinner, WdsStack, WdsText } from '@worksome/wds-components'
import { FragmentType } from '@/gql'
import { CustomFieldTypeValueInput } from '@/gql/graphql'
import { $t } from '@/composables/translation'
import FormCustomFields from '@/components/Fieldable/FormCustomFields.vue'
import {
  FieldableFormCustomFieldFragment,
  FieldableFormCustomFieldValueFragment,
} from '@/components/Fieldable/graphql/fragments'

// The hire custom fields editor, shared by the company and the worker or agency; each caller loads and saves.
withDefaults(
  defineProps<{
    open: boolean
    title: string
    fields: FragmentType<typeof FieldableFormCustomFieldFragment>[]
    fieldValues: CustomFieldTypeValueInput[]
    fieldValueFragments?: FragmentType<typeof FieldableFormCustomFieldValueFragment>[]
    errors?: { [key: string]: Ref<string> }
    loading?: boolean
    saving?: boolean
    failed?: boolean
    hideFooter?: boolean
    disableWorkerInputFields?: boolean
    dataTest?: string
  }>(),
  {
    fieldValueFragments: () => [],
    errors: () => ({}),
    loading: false,
    saving: false,
    failed: false,
    hideFooter: false,
    disableWorkerInputFields: false,
    dataTest: undefined,
  },
)

const emit = defineEmits<{
  cancel: []
  save: []
  'update:field-values': [values: CustomFieldTypeValueInput[]]
}>()
</script>
